import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MousePointer2,
  Square,
  Circle,
  Type,
  StickyNote,
  Pencil,
  ArrowRight,
  Trash2,
  Download,
  Upload,
  Plus,
  Minus,
  RotateCcw,
  Palette,
  Move,
  Save,
  FolderOpen,
  Grid3X3,
  Layers,
  Lock,
  Unlock,
  Copy,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Image,
  Link2,
  X,
  Check,
  RefreshCw,
  Users,
  Wifi,
  WifiOff,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Color palette
const COLORS = [
  '#FFE066', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE',
  '#85C1E9', '#F8B500', '#00D4FF', '#FF69B4', '#32CD32'
];

const SHAPE_COLORS = [
  '#1a1a2e', '#16213e', '#0f3460', '#e94560', '#533483',
  '#00d4ff', '#00ff88', '#ff6b6b', '#ffd93d', '#6c5ce7'
];

// User cursor colors
const CURSOR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD',
  '#F8B500', '#00D4FF', '#FF69B4', '#32CD32', '#6c5ce7'
];

// Element types
const ELEMENT_TYPES = {
  STICKY: 'sticky',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  TEXT: 'text',
  ARROW: 'arrow',
  FREEHAND: 'freehand',
  IMAGE: 'image',
  CONNECTOR: 'connector'
};

// Get random user ID if not logged in
const getUserId = () => {
  let userId = localStorage.getItem('whiteboard_user_id');
  if (!userId) {
    userId = `user_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('whiteboard_user_id', userId);
  }
  return userId;
};

// Get user name (could be from auth context in real app)
const getUserName = () => {
  return localStorage.getItem('whiteboard_user_name') || 'Anonymous';
};

// Remote User Cursor Component
const RemoteCursor = ({ user, zoom }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ 
      opacity: 1, 
      scale: 1,
      x: user.cursor.x * zoom,
      y: user.cursor.y * zoom
    }}
    exit={{ opacity: 0, scale: 0.5 }}
    transition={{ duration: 0.1 }}
    className="absolute pointer-events-none z-50"
    style={{ left: 0, top: 0 }}
  >
    <div 
      className="w-4 h-4"
      style={{
        borderLeft: `3px solid ${user.color}`,
        borderTop: `3px solid ${user.color}`,
        transform: 'rotate(-45deg)'
      }}
    />
    <div 
      className="absolute top-4 left-2 px-2 py-0.5 rounded text-xs text-white whitespace-nowrap"
      style={{ backgroundColor: user.color }}
    >
      {user.user_name}
    </div>
  </motion.div>
);

// Toolbar Component
const Toolbar = ({ activeTool, setActiveTool, onAction, zoom, setZoom, isConnected, userCount }) => {
  const tools = [
    { id: 'select', icon: MousePointer2, label: 'Select' },
    { id: 'sticky', icon: StickyNote, label: 'Sticky Note' },
    { id: 'rectangle', icon: Square, label: 'Rectangle' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'text', icon: Type, label: 'Text' },
    { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
    { id: 'freehand', icon: Pencil, label: 'Draw' },
    { id: 'connector', icon: Link2, label: 'Connector' },
  ];

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center space-x-1 bg-slate-800/95 backdrop-blur-sm rounded-xl p-2 shadow-xl border border-white/10 z-20">
      {tools.map(tool => (
        <button
          key={tool.id}
          onClick={() => setActiveTool(tool.id)}
          className={`p-2.5 rounded-lg transition-all ${
            activeTool === tool.id
              ? 'bg-neon-cyan text-slate-900'
              : 'text-slate-400 hover:text-white hover:bg-white/10'
          }`}
          title={tool.label}
        >
          <tool.icon className="w-5 h-5" />
        </button>
      ))}
      
      <div className="w-px h-8 bg-white/10 mx-2" />
      
      {/* Zoom controls */}
      <button
        onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
        className="p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
        title="Zoom Out"
      >
        <ZoomOut className="w-5 h-5" />
      </button>
      <span className="text-sm text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
      <button
        onClick={() => setZoom(Math.min(2, zoom + 0.25))}
        className="p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
        title="Zoom In"
      >
        <ZoomIn className="w-5 h-5" />
      </button>
      
      <div className="w-px h-8 bg-white/10 mx-2" />
      
      {/* Actions */}
      <button
        onClick={() => onAction('undo')}
        className="p-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
        title="Undo"
      >
        <RotateCcw className="w-5 h-5" />
      </button>
      <button
        onClick={() => onAction('clear')}
        className="p-2.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10"
        title="Clear All"
      >
        <Trash2 className="w-5 h-5" />
      </button>
      
      <div className="w-px h-8 bg-white/10 mx-2" />
      
      {/* Connection status & user count */}
      <div className="flex items-center space-x-2 px-2">
        {isConnected ? (
          <Wifi className="w-4 h-4 text-green-400" />
        ) : (
          <WifiOff className="w-4 h-4 text-red-400" />
        )}
        {userCount > 0 && (
          <div className="flex items-center space-x-1 text-slate-400">
            <Users className="w-4 h-4" />
            <span className="text-xs">{userCount}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Properties Panel
const PropertiesPanel = ({ selectedElement, onUpdate, onDelete, onDuplicate }) => {
  if (!selectedElement) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="absolute top-4 right-4 w-64 bg-slate-800/95 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-white/10 z-20"
    >
      <h3 className="text-sm font-medium text-white mb-3">Properties</h3>
      
      {/* Color */}
      {selectedElement.type !== 'freehand' && (
        <div className="mb-4">
          <label className="text-xs text-slate-400 mb-2 block">Color</label>
          <div className="flex flex-wrap gap-1">
            {(selectedElement.type === 'sticky' ? COLORS : SHAPE_COLORS).map(color => (
              <button
                key={color}
                onClick={() => onUpdate({ color })}
                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                  selectedElement.color === color ? 'border-white' : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Text content for sticky notes and text */}
      {(selectedElement.type === 'sticky' || selectedElement.type === 'text') && (
        <div className="mb-4">
          <label className="text-xs text-slate-400 mb-2 block">Content</label>
          <textarea
            value={selectedElement.content || ''}
            onChange={(e) => onUpdate({ content: e.target.value })}
            className="w-full px-3 py-2 bg-white/5 rounded-lg text-white text-sm resize-none border border-white/10 focus:border-neon-cyan/50 focus:outline-none"
            rows={3}
            placeholder="Enter text..."
          />
        </div>
      )}

      {/* Size for shapes */}
      {['rectangle', 'circle', 'sticky'].includes(selectedElement.type) && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Width</label>
            <input
              type="number"
              value={selectedElement.width || 100}
              onChange={(e) => onUpdate({ width: parseInt(e.target.value) || 100 })}
              className="w-full px-2 py-1 bg-white/5 rounded text-white text-sm border border-white/10"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Height</label>
            <input
              type="number"
              value={selectedElement.height || 100}
              onChange={(e) => onUpdate({ height: parseInt(e.target.value) || 100 })}
              className="w-full px-2 py-1 bg-white/5 rounded text-white text-sm border border-white/10"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex space-x-2 pt-3 border-t border-white/10">
        <button
          onClick={onDuplicate}
          className="flex-1 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm flex items-center justify-center space-x-1"
        >
          <Copy className="w-4 h-4" />
          <span>Duplicate</span>
        </button>
        <button
          onClick={onDelete}
          className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 text-sm flex items-center justify-center space-x-1"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete</span>
        </button>
      </div>
    </motion.div>
  );
};

// Sticky Note Component
const StickyNoteElement = ({ element, isSelected, onSelect, onUpdate, lockedBy }) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div
      className={`absolute cursor-move select-none ${isSelected ? 'ring-2 ring-neon-cyan' : ''} ${lockedBy ? 'ring-2 ring-orange-400' : ''}`}
      style={{
        left: element.x,
        top: element.y,
        width: element.width || 200,
        height: element.height || 150,
        transform: `rotate(${element.rotation || 0}deg)`,
        zIndex: element.zIndex || 1
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
      onDoubleClick={() => !lockedBy && setIsEditing(true)}
    >
      <div
        className="w-full h-full rounded-lg shadow-lg p-3 flex flex-col"
        style={{ backgroundColor: element.color || '#FFE066' }}
      >
        {/* Fold effect */}
        <div 
          className="absolute top-0 right-0 w-8 h-8"
          style={{
            background: `linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.1) 50%)`,
          }}
        />
        
        {/* Lock indicator */}
        {lockedBy && (
          <div className="absolute -top-2 -right-2 bg-orange-400 rounded-full p-1">
            <Lock className="w-3 h-3 text-white" />
          </div>
        )}
        
        {isEditing ? (
          <textarea
            autoFocus
            value={element.content || ''}
            onChange={(e) => onUpdate(element.id, { content: e.target.value })}
            onBlur={() => setIsEditing(false)}
            className="flex-1 bg-transparent text-slate-800 text-sm resize-none outline-none"
            placeholder="Write something..."
          />
        ) : (
          <p className="flex-1 text-slate-800 text-sm whitespace-pre-wrap overflow-hidden">
            {element.content || 'Double-click to edit'}
          </p>
        )}
      </div>
    </div>
  );
};

// Rectangle Element
const RectangleElement = ({ element, isSelected, onSelect, lockedBy }) => (
  <div
    className={`absolute cursor-move ${isSelected ? 'ring-2 ring-neon-cyan' : ''} ${lockedBy ? 'ring-2 ring-orange-400' : ''}`}
    style={{
      left: element.x,
      top: element.y,
      width: element.width || 150,
      height: element.height || 100,
      backgroundColor: element.color || '#1a1a2e',
      borderRadius: element.borderRadius || 8,
      border: `2px solid ${element.borderColor || 'rgba(255,255,255,0.2)'}`,
      zIndex: element.zIndex || 1
    }}
    onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
  >
    {lockedBy && (
      <div className="absolute -top-2 -right-2 bg-orange-400 rounded-full p-1">
        <Lock className="w-3 h-3 text-white" />
      </div>
    )}
  </div>
);

// Circle Element
const CircleElement = ({ element, isSelected, onSelect, lockedBy }) => (
  <div
    className={`absolute cursor-move rounded-full ${isSelected ? 'ring-2 ring-neon-cyan' : ''} ${lockedBy ? 'ring-2 ring-orange-400' : ''}`}
    style={{
      left: element.x,
      top: element.y,
      width: element.width || 100,
      height: element.height || 100,
      backgroundColor: element.color || '#1a1a2e',
      border: `2px solid ${element.borderColor || 'rgba(255,255,255,0.2)'}`,
      zIndex: element.zIndex || 1
    }}
    onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
  >
    {lockedBy && (
      <div className="absolute -top-2 -right-2 bg-orange-400 rounded-full p-1">
        <Lock className="w-3 h-3 text-white" />
      </div>
    )}
  </div>
);

// Text Element
const TextElement = ({ element, isSelected, onSelect, onUpdate, lockedBy }) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div
      className={`absolute cursor-move ${isSelected ? 'ring-2 ring-neon-cyan ring-offset-2 ring-offset-transparent' : ''}`}
      style={{
        left: element.x,
        top: element.y,
        minWidth: 100,
        zIndex: element.zIndex || 1
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
      onDoubleClick={() => !lockedBy && setIsEditing(true)}
    >
      {isEditing ? (
        <input
          autoFocus
          type="text"
          value={element.content || ''}
          onChange={(e) => onUpdate(element.id, { content: e.target.value })}
          onBlur={() => setIsEditing(false)}
          onKeyDown={(e) => e.key === 'Enter' && setIsEditing(false)}
          className="bg-transparent text-white text-lg font-medium outline-none border-b-2 border-neon-cyan"
          style={{ color: element.color || '#ffffff' }}
        />
      ) : (
        <p 
          className="text-lg font-medium"
          style={{ color: element.color || '#ffffff' }}
        >
          {element.content || 'Double-click to edit'}
        </p>
      )}
    </div>
  );
};

// Arrow/Connector Element
const ArrowElement = ({ element, isSelected, onSelect }) => {
  const startX = element.x;
  const startY = element.y;
  const endX = element.endX || element.x + 100;
  const endY = element.endY || element.y;

  return (
    <svg
      className={`absolute pointer-events-none ${isSelected ? 'filter drop-shadow-[0_0_4px_#00d4ff]' : ''}`}
      style={{
        left: Math.min(startX, endX) - 10,
        top: Math.min(startY, endY) - 10,
        width: Math.abs(endX - startX) + 20,
        height: Math.abs(endY - startY) + 20,
        zIndex: element.zIndex || 1
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
    >
      <defs>
        <marker
          id={`arrowhead-${element.id}`}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            fill={element.color || '#00d4ff'}
          />
        </marker>
      </defs>
      <line
        x1={startX - Math.min(startX, endX) + 10}
        y1={startY - Math.min(startY, endY) + 10}
        x2={endX - Math.min(startX, endX) + 10}
        y2={endY - Math.min(startY, endY) + 10}
        stroke={element.color || '#00d4ff'}
        strokeWidth="2"
        markerEnd={`url(#arrowhead-${element.id})`}
        className="pointer-events-auto cursor-move"
      />
    </svg>
  );
};

// Freehand Drawing Element
const FreehandElement = ({ element, isSelected, onSelect }) => {
  if (!element.points || element.points.length < 2) return null;

  const minX = Math.min(...element.points.map(p => p.x));
  const minY = Math.min(...element.points.map(p => p.y));
  const maxX = Math.max(...element.points.map(p => p.x));
  const maxY = Math.max(...element.points.map(p => p.y));

  const pathData = element.points.reduce((acc, point, i) => {
    const x = point.x - minX + 5;
    const y = point.y - minY + 5;
    return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
  }, '');

  return (
    <svg
      className={`absolute pointer-events-none ${isSelected ? 'filter drop-shadow-[0_0_4px_#00d4ff]' : ''}`}
      style={{
        left: minX - 5,
        top: minY - 5,
        width: maxX - minX + 10,
        height: maxY - minY + 10,
        zIndex: element.zIndex || 1
      }}
      onClick={(e) => { e.stopPropagation(); onSelect(element.id); }}
    >
      <path
        d={pathData}
        stroke={element.color || '#00d4ff'}
        strokeWidth={element.strokeWidth || 3}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="pointer-events-auto cursor-move"
      />
    </svg>
  );
};

// Main Whiteboard Component
export const Whiteboard = () => {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [activeTool, setActiveTool] = useState('select');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [boards, setBoards] = useState([]);
  const [currentBoardId, setCurrentBoardId] = useState(null);
  const [boardName, setBoardName] = useState('Untitled Board');
  const [showBoardList, setShowBoardList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Real-time collaboration state
  const [isConnected, setIsConnected] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [lockedElements, setLockedElements] = useState({}); // element_id -> user_name

  const selectedElement = elements.find(el => el.id === selectedId);
  const userId = getUserId();
  const userName = getUserName();

  // Connect to WebSocket when board is loaded
  const connectWebSocket = useCallback((boardId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${WS_URL}/api/whiteboard/ws/${boardId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      toast.success('Connected to collaboration server');
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    wsRef.current = ws;
  }, [userId, userName]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'sync':
        // Full board state sync
        if (data.board) {
          setElements(data.board.elements || []);
          setBoardName(data.board.name || 'Untitled Board');
        }
        if (data.users) {
          setRemoteUsers(data.users.filter(u => u.user_id !== userId).map((u, i) => ({
            ...u,
            color: CURSOR_COLORS[i % CURSOR_COLORS.length]
          })));
        }
        break;

      case 'user_joined':
        toast.info(`${data.user_name} joined the board`);
        if (data.users) {
          setRemoteUsers(data.users.filter(u => u.user_id !== userId).map((u, i) => ({
            ...u,
            color: CURSOR_COLORS[i % CURSOR_COLORS.length]
          })));
        }
        break;

      case 'user_left':
        toast.info(`${data.user_name} left the board`);
        if (data.users) {
          setRemoteUsers(data.users.filter(u => u.user_id !== userId).map((u, i) => ({
            ...u,
            color: CURSOR_COLORS[i % CURSOR_COLORS.length]
          })));
        }
        // Clean up locks from leaving user
        setLockedElements(prev => {
          const newLocks = { ...prev };
          Object.keys(newLocks).forEach(elId => {
            if (newLocks[elId] === data.user_name) {
              delete newLocks[elId];
            }
          });
          return newLocks;
        });
        break;

      case 'element_added':
        setElements(prev => [...prev, data.element]);
        break;

      case 'element_updated':
        setElements(prev => prev.map(el => 
          el.id === data.element_id ? { ...el, ...data.updates } : el
        ));
        break;

      case 'element_deleted':
        setElements(prev => prev.filter(el => el.id !== data.element_id));
        break;

      case 'cursor_moved':
        setRemoteUsers(prev => prev.map(u => 
          u.user_id === data.user_id 
            ? { ...u, cursor: { x: data.x, y: data.y } }
            : u
        ));
        break;

      case 'element_locked':
        setLockedElements(prev => ({ ...prev, [data.element_id]: data.user_name }));
        break;

      case 'element_unlocked':
        setLockedElements(prev => {
          const newLocks = { ...prev };
          delete newLocks[data.element_id];
          return newLocks;
        });
        break;

      case 'error':
        toast.error(data.message || 'An error occurred');
        break;
    }
  }, [userId]);

  // Send WebSocket message
  const sendWSMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Fetch boards
  const fetchBoards = async () => {
    try {
      const res = await fetch(`${API_URL}/api/whiteboard/boards`);
      if (res.ok) {
        const data = await res.json();
        setBoards(data.boards || []);
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    }
  };

  useEffect(() => {
    fetchBoards();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Track mouse for cursor broadcast
  const handleMouseMove = useCallback((e) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    // Broadcast cursor position (throttled by WebSocket)
    if (isConnected) {
      sendWSMessage({ type: 'cursor_move', x, y });
    }

    // Handle drawing
    if (isDrawing && activeTool === 'freehand') {
      setCurrentPath(prev => [...prev, { x, y }]);
    }
  }, [pan, zoom, isConnected, sendWSMessage, isDrawing, activeTool]);

  // Save to history
  const saveToHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newElements));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Undo
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(history[historyIndex - 1]));
    }
  };

  // Get mouse position relative to canvas
  const getMousePos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom
    };
  };

  // Create new element
  const createNewElement = (type, pos) => {
    const baseElement = {
      id: `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      x: pos.x,
      y: pos.y,
      zIndex: elements.length + 1,
      createdAt: new Date().toISOString()
    };

    switch (type) {
      case 'sticky':
        return { ...baseElement, type: 'sticky', width: 200, height: 150, color: COLORS[0], content: '' };
      case 'rectangle':
        return { ...baseElement, type: 'rectangle', width: 150, height: 100, color: '#1a1a2e' };
      case 'circle':
        return { ...baseElement, type: 'circle', width: 100, height: 100, color: '#1a1a2e' };
      case 'text':
        return { ...baseElement, type: 'text', content: 'Text', color: '#ffffff' };
      case 'arrow':
        return { ...baseElement, type: 'arrow', endX: pos.x + 100, endY: pos.y, color: '#00d4ff' };
      default:
        return null;
    }
  };

  // Handle canvas click
  const handleCanvasClick = (e) => {
    if (activeTool === 'select') {
      setSelectedId(null);
      return;
    }

    const pos = getMousePos(e);
    const newElement = createNewElement(activeTool, pos);
    
    if (newElement) {
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveToHistory(newElements);
      setSelectedId(newElement.id);
      
      // Broadcast to other users
      sendWSMessage({ type: 'element_add', element: newElement });
      
      if (activeTool !== 'freehand') {
        setActiveTool('select');
      }
    }
  };

  // Handle mouse down for drawing
  const handleMouseDown = (e) => {
    if (activeTool === 'freehand') {
      setIsDrawing(true);
      const pos = getMousePos(e);
      setCurrentPath([pos]);
    }
  };

  // Handle mouse up for drawing
  const handleMouseUp = () => {
    if (isDrawing && currentPath.length > 1) {
      const newElement = {
        id: `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'freehand',
        points: currentPath,
        color: '#00d4ff',
        strokeWidth: 3,
        zIndex: elements.length + 1,
        createdAt: new Date().toISOString()
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveToHistory(newElements);
      
      // Broadcast to other users
      sendWSMessage({ type: 'element_add', element: newElement });
    }
    setIsDrawing(false);
    setCurrentPath([]);
  };

  // Update element
  const updateElement = (id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
    saveToHistory(newElements);
    
    // Broadcast to other users
    sendWSMessage({ type: 'element_update', element_id: id, updates });
  };

  // Delete element
  const deleteElement = () => {
    if (!selectedId) return;
    const newElements = elements.filter(el => el.id !== selectedId);
    setElements(newElements);
    saveToHistory(newElements);
    
    // Broadcast to other users
    sendWSMessage({ type: 'element_delete', element_id: selectedId });
    
    setSelectedId(null);
  };

  // Duplicate element
  const duplicateElement = () => {
    if (!selectedElement) return;
    const newElement = {
      ...selectedElement,
      id: `el_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      x: selectedElement.x + 20,
      y: selectedElement.y + 20,
      zIndex: elements.length + 1
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    
    // Broadcast to other users
    sendWSMessage({ type: 'element_add', element: newElement });
    
    setSelectedId(newElement.id);
  };

  // Clear all
  const clearAll = () => {
    if (confirm('Clear all elements?')) {
      // Delete each element one by one for proper sync
      elements.forEach(el => {
        sendWSMessage({ type: 'element_delete', element_id: el.id });
      });
      setElements([]);
      saveToHistory([]);
      setSelectedId(null);
    }
  };

  // Handle toolbar actions
  const handleAction = (action) => {
    switch (action) {
      case 'undo':
        handleUndo();
        break;
      case 'clear':
        clearAll();
        break;
    }
  };

  // Save board
  const saveBoard = async () => {
    setLoading(true);
    try {
      const method = currentBoardId ? 'PUT' : 'POST';
      const url = currentBoardId 
        ? `${API_URL}/api/whiteboard/boards/${currentBoardId}`
        : `${API_URL}/api/whiteboard/boards`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: boardName,
          elements: elements
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (!currentBoardId && data.board?.id) {
          setCurrentBoardId(data.board.id);
          // Connect WebSocket after creating board
          connectWebSocket(data.board.id);
        }
        toast.success('Board saved');
        fetchBoards();
      }
    } catch (error) {
      toast.error('Failed to save board');
    } finally {
      setLoading(false);
    }
  };

  // Load board
  const loadBoard = async (boardId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/whiteboard/boards/${boardId}`);
      if (res.ok) {
        const data = await res.json();
        setElements(data.elements || []);
        setBoardName(data.name || 'Untitled');
        setCurrentBoardId(boardId);
        setShowBoardList(false);
        saveToHistory(data.elements || []);
        
        // Connect WebSocket for real-time collaboration
        connectWebSocket(boardId);
        
        toast.success('Board loaded');
      }
    } catch (error) {
      toast.error('Failed to load board');
    } finally {
      setLoading(false);
    }
  };

  // New board
  const newBoard = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setElements([]);
    setBoardName('Untitled Board');
    setCurrentBoardId(null);
    setHistory([]);
    setHistoryIndex(-1);
    setShowBoardList(false);
    setIsConnected(false);
    setRemoteUsers([]);
    setLockedElements({});
  };

  // Render element
  const renderElement = (element) => {
    const isSelected = selectedId === element.id;
    const lockedBy = lockedElements[element.id];
    const commonProps = {
      element,
      isSelected,
      onSelect: setSelectedId,
      onUpdate: updateElement,
      lockedBy
    };

    switch (element.type) {
      case 'sticky':
        return <StickyNoteElement key={element.id} {...commonProps} />;
      case 'rectangle':
        return <RectangleElement key={element.id} {...commonProps} />;
      case 'circle':
        return <CircleElement key={element.id} {...commonProps} />;
      case 'text':
        return <TextElement key={element.id} {...commonProps} />;
      case 'arrow':
        return <ArrowElement key={element.id} {...commonProps} />;
      case 'freehand':
        return <FreehandElement key={element.id} {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-[calc(100vh-200px)] min-h-[600px] relative bg-slate-900 rounded-xl overflow-hidden border border-white/10" data-testid="whiteboard">
      {/* Header */}
      <div className="absolute top-4 left-4 flex items-center space-x-3 z-20">
        <button
          onClick={() => setShowBoardList(true)}
          className="p-2.5 bg-slate-800/95 backdrop-blur-sm rounded-lg text-slate-400 hover:text-white border border-white/10"
          title="My Boards"
        >
          <FolderOpen className="w-5 h-5" />
        </button>
        <input
          type="text"
          value={boardName}
          onChange={(e) => setBoardName(e.target.value)}
          className="px-4 py-2 bg-slate-800/95 backdrop-blur-sm rounded-lg text-white font-medium border border-white/10 focus:border-neon-cyan/50 focus:outline-none"
        />
        <button
          onClick={saveBoard}
          disabled={loading}
          className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg font-medium hover:bg-neon-cyan/80 flex items-center space-x-2"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>Save</span>
        </button>
        <button
          onClick={newBoard}
          className="p-2.5 bg-slate-800/95 backdrop-blur-sm rounded-lg text-slate-400 hover:text-white border border-white/10"
          title="New Board"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      {/* Online users indicator */}
      {remoteUsers.length > 0 && (
        <div className="absolute top-4 right-4 flex items-center space-x-2 bg-slate-800/95 backdrop-blur-sm rounded-lg px-3 py-2 border border-white/10 z-20">
          <Users className="w-4 h-4 text-green-400" />
          <span className="text-sm text-white">{remoteUsers.length + 1} online</span>
          <div className="flex -space-x-2">
            {remoteUsers.slice(0, 3).map((user, i) => (
              <div 
                key={user.user_id}
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-slate-800"
                style={{ backgroundColor: user.color }}
                title={user.user_name}
              >
                {user.user_name.charAt(0).toUpperCase()}
              </div>
            ))}
            {remoteUsers.length > 3 && (
              <div className="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center text-xs font-bold text-white border-2 border-slate-800">
                +{remoteUsers.length - 3}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <Toolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        onAction={handleAction}
        zoom={zoom}
        setZoom={setZoom}
        isConnected={isConnected}
        userCount={remoteUsers.length + 1}
      />

      {/* Properties Panel */}
      <AnimatePresence>
        {selectedElement && (
          <PropertiesPanel
            selectedElement={selectedElement}
            onUpdate={(updates) => updateElement(selectedId, updates)}
            onDelete={deleteElement}
            onDuplicate={duplicateElement}
          />
        )}
      </AnimatePresence>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="w-full h-full cursor-crosshair overflow-hidden"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`
        }}
        onClick={handleCanvasClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: '100%',
            height: '100%',
            position: 'relative'
          }}
        >
          {elements.map(renderElement)}
          
          {/* Remote user cursors */}
          <AnimatePresence>
            {remoteUsers.map(user => (
              <RemoteCursor key={user.user_id} user={user} zoom={1} />
            ))}
          </AnimatePresence>
          
          {/* Current drawing path */}
          {isDrawing && currentPath.length > 1 && (
            <svg
              className="absolute inset-0 pointer-events-none"
              style={{ width: '100%', height: '100%' }}
            >
              <path
                d={currentPath.reduce((acc, point, i) => 
                  i === 0 ? `M ${point.x} ${point.y}` : `${acc} L ${point.x} ${point.y}`, ''
                )}
                stroke="#00d4ff"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>

      {/* Board List Modal */}
      <AnimatePresence>
        {showBoardList && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
            onClick={() => setShowBoardList(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-xl p-6 w-96 max-h-[60vh] overflow-hidden border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">My Boards</h3>
                <button
                  onClick={() => setShowBoardList(false)}
                  className="p-1 rounded hover:bg-white/10 text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2 max-h-[40vh] overflow-y-auto">
                {boards.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No saved boards yet</p>
                ) : (
                  boards.map(board => (
                    <button
                      key={board.id}
                      onClick={() => loadBoard(board.id)}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        currentBoardId === board.id
                          ? 'bg-neon-cyan/20 border border-neon-cyan/50'
                          : 'bg-white/5 hover:bg-white/10 border border-transparent'
                      }`}
                    >
                      <div className="font-medium text-white">{board.name}</div>
                      <div className="text-xs text-slate-400">
                        {board.element_count || 0} elements · {new Date(board.updated_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))
                )}
              </div>

              <button
                onClick={newBoard}
                className="w-full mt-4 py-2 border-2 border-dashed border-white/20 rounded-lg text-slate-400 hover:text-white hover:border-neon-cyan/30 flex items-center justify-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Create New Board</span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help text */}
      <div className="absolute bottom-4 left-4 text-xs text-slate-500 z-10">
        Click to add elements • Double-click to edit • {isConnected ? '🟢 Real-time sync enabled' : '⚪ Save to enable collaboration'}
      </div>
    </div>
  );
};

export default Whiteboard;
